import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { API_URL } from '../utils/config';
import axios from 'axios';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, token } = useAuth();
  const { clearCart, removeFromCart } = useCart();

  const [selectedItems, setSelectedItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [error, setError] = useState(null);
  
  // Courier selection states
  const [useCourier, setUseCourier] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState('');

  const [newAddress, setNewAddress] = useState({
    label: 'Home',
    fullName: '',
    phone: '',
    address: '',
    city: '',
    postalCode: ''
  });

  // 13 districts of Bangladesh (divisional cities)
  const BANGLADESH_CITIES = [
    'Dhaka', 'Chittagong', 'Khulna', 'Rajshahi', 'Sylhet',
    'Barishal', 'Rangpur', 'Mymensingh', 'Comilla', 'Narayanganj',
    'Gazipur', 'Jessore', 'Bogura'
  ];

  // Shipping fee: 80৳ for Khulna, 130৳ for all other cities
  const getShippingFee = (city) => {
    if (!city) return 130;
    return city.toLowerCase() === 'khulna' ? 80 : 130;
  };

  // Popular couriers in Bangladesh
  const couriers = [
    { id: 'steadfast', name: 'Steadfast', icon: 'fa-shipping-fast' },
    { id: 'pathao', name: 'Pathao Courier', icon: 'fa-motorcycle' },
    { id: 'redx', name: 'RedX', icon: 'fa-box' },
    { id: 'sundarban', name: 'Sundarban Courier', icon: 'fa-truck' },
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Get selected items from location state
    if (location.state?.selectedItems) {
      setSelectedItems(location.state.selectedItems);
    } else {
      navigate('/cart');
      return;
    }

    fetchAddresses();
  }, [isAuthenticated, navigate, location]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userAddresses = response.data.addresses || [];
      setAddresses(userAddresses);
      
      // Auto-select default address
      const defaultAddr = userAddresses.find(addr => addr.isDefault);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr._id);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setError('Failed to load addresses. Please refresh the page.');
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/img/products/f1.jpg';
    if (imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  const calculateSubtotal = () => {
    return selectedItems.reduce((total, item) =>
      total + (item.price * item.quantity), 0
    );
  };

  const getSelectedCity = () => {
    if (!selectedAddress) return '';
    const address = addresses.find(addr => addr._id === selectedAddress);
    return address?.city || '';
  };

  const calculateTotal = () => {
    return calculateSubtotal() + getShippingFee(getSelectedCity());
  };

  const handleAddressInputChange = (e) => {
    setNewAddress({
      ...newAddress,
      [e.target.name]: e.target.value
    });
  };

  const validatePhone = (phone) => {
    // Basic Bangladesh phone number validation (11 digits)
    const phoneRegex = /^01[0-9]{9}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ''));
  };

  const validatePostalCode = (postalCode) => {
    // Basic postal code validation (4 digits for Bangladesh)
    const postalRegex = /^[0-9]{4}$/;
    return postalRegex.test(postalCode);
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!validatePhone(newAddress.phone)) {
      alert('Please enter a valid phone number (11 digits starting with 01)');
      return;
    }

    if (!validatePostalCode(newAddress.postalCode)) {
      alert('Please enter a valid 4-digit postal code');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${API_URL}/api/users/addresses`,
        newAddress,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchAddresses();
      setShowAddressForm(false);
      setNewAddress({
        label: 'Home',
        fullName: '',
        phone: '',
        address: '',
        city: '',
        postalCode: ''
      });
      alert('Address added successfully!');
    } catch (error) {
      console.error('Error adding address:', error);
      alert(error.response?.data?.message || 'Error adding address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      alert('Please select a delivery address');
      return;
    }

    if (!paymentMethod) {
      alert('Please select a payment method');
      return;
    }

    // Validate courier selection if enabled
    if (useCourier && !selectedCourier) {
      alert('Please select a courier service');
      return;
    }

    if (selectedItems.length === 0) {
      alert('Your cart is empty');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const address = addresses.find(addr => addr._id === selectedAddress);
      
      if (!address) {
        throw new Error('Selected address not found');
      }

      const shippingFee = getShippingFee(address.city);
      const orderData = {
        items: selectedItems.map(item => ({
          product: item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          color: item.selectedColor || item.color || null,
          size: item.selectedSize || item.size || null
        })),
        shippingAddress: {
          fullName: address.fullName,
          phone: address.phone,
          address: address.address,
          city: address.city,
          postalCode: address.postalCode
        },
        paymentMethod,
        shippingFee: shippingFee,
        totalAmount: calculateSubtotal() + shippingFee,
        // Add courier info if selected
        ...(useCourier && selectedCourier && {
          courierService: {
            enabled: true,
            courierName: couriers.find(c => c.id === selectedCourier)?.name || selectedCourier
          }
        })
      };

      const response = await axios.post(
        `${API_URL}/api/orders`,
        orderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Clear ordered items from cart
      selectedItems.forEach(item => {
        if (removeFromCart) {
          removeFromCart(item.cartItemId || item._id);
        }
      });

      // Show success message
      alert(`Order placed successfully! Order #${response.data.orderNumber}`);
      
      // Navigate to orders page
      navigate('/orders', { 
        state: { 
          orderSuccess: true, 
          orderNumber: response.data.orderNumber 
        } 
      });

    } catch (error) {
      console.error('Error placing order:', error);
      const errorMessage = error.response?.data?.message || 'Error placing order. Please try again.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getLabelIcon = (label) => {
    const icons = {
      Home: 'fa-home',
      Work: 'fa-briefcase',
      Other: 'fa-map-marker-alt'
    };
    return icons[label] || 'fa-map-marker-alt';
  };

  return (
    <div className="checkout-page">
      {/* Header */}
      <section className="checkout-header">
        <div className="checkout-header-content">
          <h2>
            <i className="fas fa-lock"></i>
            Secure Checkout
          </h2>
          <p>Complete your order in a few simple steps</p>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="checkout-container section-p1">
        {/* Main Content */}
        <div className="checkout-main">
          {/* Delivery Address Section */}
          <div className="checkout-section">
            <div className="section-header">
              <h3>
                <span className="step-number">1</span>
                <i className="fas fa-map-marker-alt"></i>
                Delivery Address
              </h3>
              <button 
                className="btn-add-new"
                onClick={() => setShowAddressForm(!showAddressForm)}
                disabled={loading}
              >
                <i className="fas fa-plus"></i>
                Add New Address
              </button>
            </div>

            {/* Add Address Form */}
            {showAddressForm && (
              <form onSubmit={handleAddAddress} className="address-form">
                <div className="form-row">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name *"
                    value={newAddress.fullName}
                    onChange={handleAddressInputChange}
                    required
                    disabled={loading}
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number * (e.g., 01712345678)"
                    value={newAddress.phone}
                    onChange={handleAddressInputChange}
                    required
                    disabled={loading}
                    pattern="01[0-9]{9}"
                    title="Please enter a valid 11-digit phone number starting with 01"
                  />
                </div>
                <textarea
                  name="address"
                  placeholder="Street Address *"
                  value={newAddress.address}
                  onChange={handleAddressInputChange}
                  required
                  disabled={loading}
                  rows="3"
                />
                <div className="form-row">
                  <select
                    name="city"
                    value={newAddress.city}
                    onChange={handleAddressInputChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Select City *</option>
                    {BANGLADESH_CITIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="postalCode"
                    placeholder="Postal Code * (4 digits)"
                    value={newAddress.postalCode}
                    onChange={handleAddressInputChange}
                    required
                    disabled={loading}
                    pattern="[0-9]{4}"
                    title="Please enter a valid 4-digit postal code"
                  />
                </div>
                <div className="form-row">
                  <select
                    name="label"
                    value={newAddress.label}
                    onChange={handleAddressInputChange}
                    disabled={loading}
                  >
                    <option value="Home">Home</option>
                    <option value="Work">Work</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-save" disabled={loading}>
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Saving...
                      </>
                    ) : (
                      'Save Address'
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => setShowAddressForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Address List */}
            <div className="address-list">
              {addresses.length === 0 ? (
                <div className="no-address">
                  <i className="fas fa-map-marked-alt"></i>
                  <p>No saved addresses. Please add a delivery address.</p>
                </div>
              ) : (
                addresses.map((address) => (
                  <label
                    key={address._id}
                    className={`address-card ${selectedAddress === address._id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="address"
                      value={address._id}
                      checked={selectedAddress === address._id}
                      onChange={() => setSelectedAddress(address._id)}
                      disabled={loading}
                    />
                    <div className="address-content">
                      <div className="address-header">
                        <i className={`fas ${getLabelIcon(address.label)}`}></i>
                        <strong>{address.label}</strong>
                        {address.isDefault && (
                          <span className="default-badge">Default</span>
                        )}
                      </div>
                      <p className="address-name">{address.fullName}</p>
                      <p className="address-phone">{address.phone}</p>
                      <p className="address-text">
                        {address.address}, {address.city} - {address.postalCode}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Courier Service Section */}
          <div className="checkout-section">
            <div className="section-header">
              <h3>
                <span className="step-number">2</span>
                <i className="fas fa-truck"></i>
                Delivery Method
              </h3>
            </div>

            {/* Enable Courier Checkbox */}
            <div className="courier-toggle">
              <label className="courier-checkbox-label">
                <input
                  type="checkbox"
                  checked={useCourier}
                  onChange={(e) => {
                    setUseCourier(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedCourier('');
                    }
                  }}
                  disabled={loading}
                />
                <span className="checkbox-text">
                  <i className="fas fa-shipping-fast"></i>
                  Use Courier Service (Optional)
                </span>
              </label>
              {!useCourier && (
                <p className="delivery-note">
                  <i className="fas fa-info-circle"></i>
                  Standard delivery will be used
                </p>
              )}
            </div>

            {/* Courier Selection */}
            {useCourier && (
              <div className="courier-selection">
                <div className="courier-grid">
                  {couriers.map((courier) => (
                    <label
                      key={courier.id}
                      className={`courier-card ${selectedCourier === courier.id ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="courier"
                        value={courier.id}
                        checked={selectedCourier === courier.id}
                        onChange={(e) => setSelectedCourier(e.target.value)}
                        disabled={loading}
                      />
                      <div className="courier-content">
                        <i className={`fas ${courier.icon}`}></i>
                        <strong>{courier.name}</strong>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Method Section */}
          <div className="checkout-section">
            <div className="section-header">
              <h3>
                <span className="step-number">3</span>
                <i className="fas fa-credit-card"></i>
                Payment Method
              </h3>
            </div>

            <div className="payment-methods">
              <label className={`payment-card ${paymentMethod === 'cash' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="payment"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={loading}
                />
                <div className="payment-content">
                  <i className="fas fa-money-bill-wave"></i>
                  <div>
                    <strong>Cash on Delivery</strong>
                    <p>Pay when you receive</p>
                  </div>
                </div>
              </label>

              <label className={`payment-card ${paymentMethod === 'bkash' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="payment"
                  value="bkash"
                  checked={paymentMethod === 'bkash'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={loading}
                />
                <div className="payment-content">
                  <i className="fas fa-mobile-alt"></i>
                  <div>
                    <strong>bKash</strong>
                    <p>Mobile payment</p>
                  </div>
                </div>
              </label>

              <label className={`payment-card ${paymentMethod === 'nagad' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="payment"
                  value="nagad"
                  checked={paymentMethod === 'nagad'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={loading}
                />
                <div className="payment-content">
                  <i className="fas fa-mobile-alt"></i>
                  <div>
                    <strong>Nagad</strong>
                    <p>Mobile payment</p>
                  </div>
                </div>
              </label>

              <label className={`payment-card ${paymentMethod === 'card' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="payment"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={loading}
                />
                <div className="payment-content">
                  <i className="fas fa-credit-card"></i>
                  <div>
                    <strong>Credit/Debit Card</strong>
                    <p>Visa, Mastercard</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Order Items Section */}
          <div className="checkout-section">
            <div className="section-header">
              <h3>
                <span className="step-number">4</span>
                <i className="fas fa-box"></i>
                Order Items ({selectedItems.length})
              </h3>
            </div>

            <div className="order-items">
              {selectedItems.length === 0 ? (
                <div className="no-items">
                  <i className="fas fa-shopping-bag"></i>
                  <p>No items in your order</p>
                </div>
              ) : (
                selectedItems.map((item) => (
                  <div key={item._id} className="order-item">
                    <img 
                      src={getImageUrl(item.image)} 
                      alt={item.name}
                      onError={(e) => e.target.src = '/img/products/f1.jpg'}
                    />
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <p className="item-brand">{item.brand || 'Goldvines'}</p>
                      {(item.selectedColor || item.selectedSize) && (
                        <div style={{ display: 'flex', gap: '10px', fontSize: '13px', color: '#666', margin: '4px 0' }}>
                          {item.selectedColor && <span>🎨 Color: <strong>{item.selectedColor}</strong></span>}
                          {item.selectedSize && <span>📏 Size: <strong>{item.selectedSize}</strong></span>}
                        </div>
                      )}
                      <p className="item-price">
                        {item.price}৳ × {item.quantity} = {item.price * item.quantity}৳
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Order Summary */}
        <div className="checkout-sidebar">
          <div className="order-summary">
            <h3>
              <i className="fas fa-receipt"></i>
              Order Summary
            </h3>
            
            <div className="summary-row">
              <span>Items ({selectedItems.length})</span>
              <span>{calculateSubtotal()}৳</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span style={{ color: '#e44d26', fontWeight: '600' }}>
                {selectedAddress
                  ? `${getShippingFee(getSelectedCity())}৳ (${getSelectedCity() || '—'})`
                  : 'Select address'}
              </span>
            </div>
            {useCourier && selectedCourier && (
              <div className="summary-row">
                <span>Courier Service</span>
                <span className="courier-name">
                  {couriers.find(c => c.id === selectedCourier)?.name}
                </span>
              </div>
            )}
            <div className="summary-row">
              <span>Tax</span>
              <span>0৳</span>
            </div>
            
            <div className="summary-total">
              <span>Total</span>
              <span>{calculateTotal()}৳</span>
            </div>

            <button 
              className="btn-place-order"
              onClick={handlePlaceOrder}
              disabled={loading || !selectedAddress || selectedItems.length === 0 || (useCourier && !selectedCourier)}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle"></i>
                  Place Order
                </>
              )}
            </button>

            <Link to="/cart" className="btn-back-cart">
              <i className="fas fa-arrow-left"></i>
              Back to Cart
            </Link>

            <div className="security-badges">
              <div className="badge">
                <i className="fas fa-lock"></i>
                <span>Secure Checkout</span>
              </div>
              <div className="badge">
                <i className="fas fa-shield-alt"></i>
                <span>Buyer Protection</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;